import { Component, Prop, h, Event, EventEmitter } from '@stencil/core';
import {
  formatDistance,
  formatLocationSpecialties,
  getDirectionsUrl,
  AnalyticsTracking,
  ActionType,
  AnalyticsComponentData,
  convertAnalyticsComponentData,
  getAnalyticsData,
  DigitalDataCallback,
  ClickTracker,
} from '@hpfc/fc-frontend-core';

export type SpecialtyProp = {
  name: string;
};

/**
 * Creates a text block displaying location information, including name, image, specialties, and hours.
 */
@Component({
  tag: 'fccd-location-information',
  styleUrl: 'fccd-location-information.scss',
})
export class LocationCard {
  /**
   * The name of the location.
   */
  @Prop()
  locationName: string;

  @Prop()
  locationId: string;

  /**
   * The specialties of the location.
   * Can be and array of strings or a comma delimited list of specialties.
   */
  @Prop()
  specialties: Array<SpecialtyProp>;

  /**
   * The selected specialties of the location.
   * Can be and array of strings or a comma delimited list of specialties.
   */
  @Prop()
  selectedspecialties: Array<SpecialtyProp>;

  /**
   * The distance from the user to the location.
   */
  @Prop()
  distance: number;

  /**
   * The address of the location.
   */
  @Prop()
  address: string;

  /**
   * The hours of the location.
   */
  @Prop()
  hours: string;

  /**
   *  The URL of the location's details page.
   */
  @Prop()
  url: string;

  /**
   * The latitude of the location.
   */
  @Prop()
  latitude: number;

  /**
   * The longitude of the location.
   */
  @Prop()
  longitude: number;

  /**
   * The size of the heading.
   */
  @Prop()
  headingSize: 'sm' | 'md' = 'md';

  /**
   * The display type of the location, whether it is a real physical location with an address or not.
   */
  @Prop()
  displayType?: 'PHONE' | 'PHYSICAL';

  @Prop()
  analytics: string | AnalyticsComponentData;

  /**
   * Event emitted when the card is clicked.
   */
  @Event()
  fccdClickTracker: EventEmitter<ClickTracker>;

  analyticsTracking: AnalyticsTracking;

  /**
   * For location cards that have specialties, this function will format the specialties text.
   * @param specialties The locations full list of specialties.
   * @param selectedSpecialties The current selected specialties.
   * @returns The formatted specialties text.
   */
  private getSpecialtiesText(specialties: Array<SpecialtyProp>, specialtiesSelected: Array<SpecialtyProp>) {
    const displaySpecialties = formatLocationSpecialties(this.getArray(specialties), this.getArray(specialtiesSelected));
    return displaySpecialties;
  }

  private getArray(specialties: Array<SpecialtyProp>) {
    return specialties ? specialties.map(item => item.name) : null;
  }

  getDigitalData: DigitalDataCallback = () => {
    return AnalyticsTracking.getDigitalData();
  };

  componentWillLoad() {
    this.analyticsTracking = new AnalyticsTracking(AnalyticsTracking.getDigitalData);
  }

  private handleEvent(eventType: 'click' | 'select' | 'unselect', actionType: ActionType, locationId: string, analyticsComponentData?: AnalyticsComponentData) {
    this.fccdClickTracker.emit(new ClickTracker(locationId, eventType, actionType, 'locationCard'));
    if (eventType === 'click') {
      this.analyticsTracking.sendData(getAnalyticsData(actionType, analyticsComponentData));
    }
  }

  render() {
    const { headingSize, url, locationName, address, latitude, longitude, specialties, selectedspecialties, distance, hours, analytics, locationId } = this;
    const analyticsComponentData = convertAnalyticsComponentData(analytics);
    const locationDirectionsUrl = address ? getDirectionsUrl(locationName, address, latitude, longitude) : null;
    const showLocationFeatures = this?.displayType === 'PHYSICAL';
    const specialtiesText = this.getSpecialtiesText(specialties, selectedspecialties);
    const formattedDistance = address ? formatDistance(distance) : null;
    return (
      <div class="info">
        <hp5-heading size-selection={headingSize} tag-selection="h3">
          {showLocationFeatures ? (
            <a
              href={url}
              tabindex={0}
              class="listing-name"
              onAuxClick={() => this.handleEvent('click', ActionType.locationDetails, locationId, analyticsComponentData)}
              onContextMenu={() => this.handleEvent('click', ActionType.locationDetails, locationId, analyticsComponentData)}
              onClick={() => this.handleEvent('click', ActionType.locationDetails, locationId, analyticsComponentData)}
              onTouchStart={() => this.handleEvent('select', ActionType.locationDetails, locationId, analyticsComponentData)}
              onTouchEnd={() => this.handleEvent('unselect', ActionType.locationDetails, locationId, analyticsComponentData)}
            >
              {locationName}
            </a>
          ) : (
            locationName
          )}
        </hp5-heading>
        <hp5-semantic size-selection="sm">
          {specialtiesText ? (
            <div class="spacer-xxs">
              <b>Specialties:</b> {specialtiesText}
            </div>
          ) : null}
          {showLocationFeatures && address ? (
            <div class="spacer-xxs">
              {address}&nbsp;
              {locationDirectionsUrl ? (
                <hp5-link-text-tertiary>
                  <a
                    href={locationDirectionsUrl}
                    target="_blank"
                    onAuxClick={() => this.handleEvent('click', ActionType.locationMap, locationId, analyticsComponentData)}
                    onContextMenu={() => this.handleEvent('click', ActionType.locationMap, locationId, analyticsComponentData)}
                    onClick={() => this.handleEvent('click', ActionType.locationMap, locationId, analyticsComponentData)}
                    onTouchStart={() => this.handleEvent('select', ActionType.locationMap, locationId, analyticsComponentData)}
                    onTouchEnd={() => this.handleEvent('unselect', ActionType.locationMap, locationId, analyticsComponentData)}
                  >
                    Directions
                  </a>
                </hp5-link-text-tertiary>
              ) : null}
            </div>
          ) : null}
          {showLocationFeatures && formattedDistance ? <div class="spacer-xxs">{formattedDistance}</div> : null}
        </hp5-semantic>
        {hours ? (
          <div>
            <fccd-open-hours hoursText={hours} textSize="xs" />
          </div>
        ) : null}
      </div>
    );
  }
}
